import { PrinterOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Flex, Progress, Result, Skeleton, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useLocation, useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle, usePoll } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { ShipBy } from "../orders/ShipBy";
import { CourierKindTag } from "../orders/CourierKind";
import { usePrintPicklist } from "./PaperPick";
import { MethodTag, NoWms, TaskStatusTag, WaveOrderTag, WaveStatusTag, useWmsWarehouse, type Task, type Wave, ZoneTag } from "./common";

const STATUSES = ["open", "done", "cancelled"] as const;
const STATUS_CHOICES = [...STATUSES, "all"] as const;
type WaveOrder = NonNullable<Wave["orderList"]>[number];
type Skipped = NonNullable<Wave["skipped"]>[number];

/** Gudang > Wave: orders released together for picking (Plan §6.9). */
export function Waves() {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const status = useStatusFilter(STATUS_CHOICES, "open");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const tz = me?.workspace?.timezone;
  const filters = { warehouseId: current?.id ?? "", ...(status.value !== "all" ? { status: status.value } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["waves", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/waves", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
    placeholderData: keepPreviousData,
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.wave.title")}
        </Typography.Title>
        <Button onClick={() => navigate("/app/warehouse/pools")}>{t("wms.pool.title")}</Button>
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("wms.wave.search")} />
        <StatusFilter
          filter={status}
          options={[...STATUSES.map((v) => ({ value: v, label: t(`wms.wave.status.${v}`) })), { value: "all" as const, label: t("list.allStatuses") }]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/waves/export"
          query={filters}
          filename={t("wms.wave.title")}
          columns={[
            { key: "number", label: t("wms.in.number") },
            { key: "poolName", label: t("wms.pool.name") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(STATUSES.map((v) => [v, t(`wms.wave.status.${v}`)])) },
            { key: "orders", label: t("wms.wave.orderCount"), kind: "number" },
            { key: "removed", label: t("wms.wave.removed"), kind: "number" },
            { key: "picked", label: t("wms.wave.picked"), kind: "number" },
            { key: "packed", label: t("wms.wave.packed"), kind: "number" },
            { key: "createdName", label: t("wms.count.createdBy") },
            { key: "createdAt", label: t("wms.count.createdAt"), kind: "datetime" },
            { key: "doneAt", label: t("list.completedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<Wave>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.wave.empty") }}
        onRow={(v) => ({ onClick: () => navigate(`/app/warehouse/waves/${v.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("wms.in.number"), dataIndex: "number", width: 150, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("wms.pool.name"), dataIndex: "poolName", render: (v: string) => v || t("wms.pool.other") },
          { title: t("common.status"), dataIndex: "status", width: 120, render: (s: Wave["status"]) => <WaveStatusTag status={s} /> },
          {
            title: t("wms.wave.picked"),
            width: 200,
            render: (_, v) => {
              const live = Math.max(v.orders - v.removed, 1);
              return <Progress size="small" percent={Math.round((v.picked / live) * 100)} format={() => `${v.picked}/${v.orders - v.removed}`} />;
            },
          },
          { title: t("wms.count.createdBy"), dataIndex: "createdName", width: 160 },
          { title: t("wms.count.createdAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
    </>
  );
}

/** One wave: its picking tasks and orders. */
export function WaveDetail() {
  const { waveID = "" } = useParams();
  const { t, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const paper = usePrintPicklist();
  const navigate = useNavigate();
  const location = useLocation();
  const skipped = (location.state as { skipped?: Skipped[] } | null)?.skipped ?? [];
  const tz = me?.workspace?.timezone;

  const wave = useQuery({
    queryKey: ["waves", "detail", waveID],
    queryFn: () => unwrap(api.GET("/v1/waves/{waveID}", { params: { path: { waveID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
    refetchInterval: usePoll(15_000),
  });
  useTabTitle(wave.data?.number);
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/waves/{waveID}/cancel", { params: { path: { waveID } } })),
    onSuccess: (res) => {
      queryClient.setQueryData(["waves", "detail", waveID], res);
      for (const k of ["waves", "pools", "tasks", "inventory"]) void queryClient.invalidateQueries({ queryKey: [k] });
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (wave.isError) {
    return wave.error instanceof ApiError && wave.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(wave.error)} />;
  }
  const v = wave.data;
  if (!v) return <Skeleton active />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {v.number}
          </Typography.Title>
          <WaveStatusTag status={v.status} />
          <Typography.Text type="secondary">
            {v.poolName || t("wms.pool.other")} · {v.createdName} · {formatDateTime(v.createdAt, tz)}
          </Typography.Text>
        </Space>
        {v.status === "open" && can("warehouse.manage") && (
          <Button
            danger
            loading={cancel.isPending}
            onClick={() =>
              modal.confirm({
                title: t("wms.wave.cancel"),
                content: t("wms.wave.cancelConfirm", { number: v.number }),
                okButtonProps: { danger: true },
                okText: t("wms.wave.cancel"),
                cancelText: t("common.close"),
                onOk: () => cancel.mutateAsync(),
              })
            }
          >
            {t("wms.wave.cancel")}
          </Button>
        )}
      </Flex>
      {skipped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("wms.wave.skipped", { n: skipped.length })}
          description={skipped.map((s) => `${s.externalId} (${t(s.reason === "shelf_short" ? "error.shelf_short" : "error.order_changed")})`).join(" · ")}
        />
      )}
      <Card size="small" title={t("wms.wave.tasks")} style={{ marginBottom: 16 }}>
        <Table<Task>
          size="small"
          rowKey="id"
          dataSource={v.tasks ?? []}
          pagination={false}
          onRow={(x) => ({ onClick: () => navigate(`/app/warehouse/tasks/${x.id}`), style: { cursor: "pointer" } })}
          columns={[
            {
              title: t("wms.task.number"),
              dataIndex: "number",
              width: 190,
              render: (n: string, x) => (
                <Space size={6}>
                  <Typography.Text strong className="z-num">{n}</Typography.Text>
                  <ZoneTag zone={x.zone} />
                </Space>
              ),
            },
            { title: t("wms.pool.method"), dataIndex: "method", width: 120, render: (m: string) => <MethodTag method={m} /> },
            { title: t("wms.task.stops"), dataIndex: "stops", width: 100, align: "right", render: (n: number) => <span className="z-num">{n}</span> },
            { title: t("common.status"), dataIndex: "status", width: 140, render: (s: Task["status"]) => <TaskStatusTag status={s} /> },
            {
              title: t("wms.task.progress"),
              render: (_, x) => <Progress size="small" percent={Math.round((x.doneUnits / Math.max(x.units, 1)) * 100)} format={() => `${x.doneUnits}/${x.units}`} />,
            },
            { title: t("wms.task.assigned"), dataIndex: "assignedName", width: 160, render: (n: string) => n || "—" },
            {
              width: 60,
              render: (_, x) =>
                (x.status === "open" || x.status === "in_progress") && (
                  <Tooltip title={t("wms.paper.print")}>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      loading={paper.printing === x.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        void paper.print(x.id);
                      }}
                    />
                  </Tooltip>
                ),
            },
          ]}
        />
      </Card>
      <Card size="small" title={t("wms.wave.orders", { n: v.orders - v.removed })}>
        <Table<WaveOrder>
          size="small"
          rowKey="orderId"
          dataSource={v.orderList ?? []}
          pagination={(v.orderList?.length ?? 0) > 50 ? { pageSize: 50, showSizeChanger: false } : false}
          columns={[
            {
              title: t("orders.number"),
              dataIndex: "externalId",
              render: (x: string, o) => (
                <Space orientation="vertical" size={0}>
                  {can("orders.view") ? (
                    <Typography.Link className="z-num" onClick={() => navigate(`/app/orders/${o.orderId}`)}>
                      {x}
                    </Typography.Link>
                  ) : (
                    <span className="z-num">{x}</span>
                  )}
                  {o.cancelRequested && (
                    <Tag color="error" style={{ width: "fit-content" }}>
                      {t("wms.wave.cancelAsked")}
                    </Tag>
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`platform.${o.platform}`)} · {o.carrier || "—"}
                  </Typography.Text>
                  <CourierKindTag kind={o.courierKind} />
                </Space>
              ),
            },
            { title: t("orders.shipBy"), dataIndex: "shipBy", width: 150, render: (x: string | null, o) => <ShipBy at={x} done={o.status === "shipped"} /> },
            { title: t("wms.wave.units"), dataIndex: "units", width: 80, align: "right" },
            {
              title: t("wms.pool.method"),
              dataIndex: "method",
              width: 220,
              render: (m: string, o) => (
                <Space size={4} wrap>
                  <MethodTag method={m} />
                  {o.slot != null && <Tag className="z-num">{t("wms.wave.slot", { n: o.slot })}</Tag>}
                  {o.group && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{o.group}</Typography.Text>}
                </Space>
              ),
            },
            {
              title: t("common.status"),
              dataIndex: "status",
              width: 200,
              render: (s: string, o) => (
                <Space size={4} wrap>
                  <WaveOrderTag status={s} />
                  {o.note && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t(`wms.wave.note.${o.note as "short"}`)}</Typography.Text>}
                </Space>
              ),
            },
            {
              title: t("wms.task.number"),
              dataIndex: "taskNumber",
              width: 160,
              render: (x: string | null, o) =>
                o.zones.length > 1 ? (
                  <Space size={4} wrap>
                    {o.zones.map((z) => (
                      <ZoneTag key={z} zone={z} />
                    ))}
                  </Space>
                ) : (
                  <span className="z-num">{x ?? "—"}</span>
                ),
            },
          ]}
        />
      </Card>
    </>
  );
}
